/**
 * Shared-property model for FlowV2 Advanced + Pinned multi-select.
 * Schemas keep only names whose editor shape matches on every icon.
 * Display values are the shared value, or blank when the icons differ.
 */

function cloneValue(value, seen = new WeakMap()) {
    if (value === null || typeof value !== 'object')
        return value;
    if (seen.has(value))
        return seen.get(value);
    if (Array.isArray(value)) {
        const copy = [];
        seen.set(value, copy);
        for (const item of value)
            copy.push(cloneValue(item, seen));
        return copy;
    }
    const copy = {};
    seen.set(value, copy);
    for (const key of Object.keys(value))
        copy[key] = cloneValue(value[key], seen);
    return copy;
}

function isPlainObject(value) {
    return !!value && typeof value === 'object' && !Array.isArray(value);
}

function deepEqual(a, b) {
    if (a === b)
        return true;
    if (a == null || b == null || typeof a !== 'object' || typeof b !== 'object')
        return false;
    if (Array.isArray(a) || Array.isArray(b)) {
        if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length)
            return false;
        for (let i = 0; i < a.length; i++) {
            if (!deepEqual(a[i], b[i]))
                return false;
        }
        return true;
    }
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length)
        return false;
    for (const key of aKeys) {
        if (!Object.prototype.hasOwnProperty.call(b, key) || !deepEqual(a[key], b[key]))
            return false;
    }
    return true;
}

/** Shape of an array item, including nested property names and types. */
function propertySignature(prop) {
    if (!prop || typeof prop !== 'object')
        return '';
    const type = prop.type || '';
    if (type === 'object') {
        const kids = Object.keys(prop.properties || {}).sort()
            .map((key) => `${key}:${propertySignature(prop.properties[key])}`);
        return `object(${kids.join(',')})`;
    }
    if (type === 'array')
        return `array(${propertySignature(prop.items)})`;
    return editorKey(prop);
}

/**
 * Editor identity: type, format, choice list, and lookup URL.
 * Object children are compared separately so partial overlap can remain.
 */
function editorKey(prop) {
    const type = prop?.type || '';
    const format = prop?.format || '';
    const lookup = prop?.formatData?.LookupURL || '';
    const map = Array.isArray(prop?.titleMap)
        ? prop.titleMap.map((entry) => `${entry?.name}=${entry?.value}`).join('|')
        : '';
    const items = type === 'array' ? propertySignature(prop.items) : '';
    return `${type}|${format}|${lookup}|${map}|${items}`;
}

function stripCompilerErrors(node, seen = new WeakSet()) {
    if (!node || typeof node !== 'object' || seen.has(node))
        return;
    seen.add(node);
    if (node.formatData && typeof node.formatData === 'object') {
        delete node.formatData.error;
        delete node.formatData.buttons;
    }
    if (node.properties) {
        for (const key of Object.keys(node.properties))
            stripCompilerErrors(node.properties[key], seen);
    }
    if (node.items)
        stripCompilerErrors(node.items, seen);
}

function intersectProperties(propertySets) {
    const first = propertySets[0] || {};
    const out = {};
    for (const key of Object.keys(first)) {
        const nodes = propertySets.map((props) => props?.[key]);
        if (nodes.some((node) => !node || typeof node !== 'object'))
            continue;
        const signature = editorKey(nodes[0]);
        if (!nodes.every((node) => editorKey(node) === signature))
            continue;

        const kept = cloneValue(nodes[0]);
        if (kept.type === 'object') {
            kept.properties = intersectProperties(nodes.map((node) => node.properties || {}));
            if (!Object.keys(kept.properties).length)
                continue;
        }
        out[key] = kept;
    }
    return out;
}

/**
 * @param {object[]} schemas Root icon schemas (API / Arguments / Output / MatchPorts)
 * @returns {object}
 */
function intersectObjectSchemas(schemas) {
    const list = (schemas || []).filter((schema) => schema && schema.type === 'object' && schema.properties);
    if (!list.length)
        return { type: 'object', properties: {} };

    const base = cloneValue(list[0]);
    base.properties = intersectProperties(list.map((schema) => schema.properties));
    const description = list[0].description ?? '';
    if (list.every((schema) => (schema.description ?? '') === description))
        base.description = description;
    else
        delete base.description;
    stripCompilerErrors(base);
    return base;
}

/**
 * @param {object} schema
 * @param {Array} values One value per selected icon, at this schema node
 */
function buildMergedValue(schema, values) {
    const list = values || [];
    if (schema?.type === 'object' && schema.properties) {
        const result = {};
        for (const key of Object.keys(schema.properties)) {
            const childValues = list.map((value) => (
                isPlainObject(value) ? value[key] : undefined
            ));
            result[key] = buildMergedValue(schema.properties[key], childValues);
        }
        return result;
    }
    if (schema?.type === 'array') {
        if (list.length && list.every((value) => deepEqual(value, list[0])))
            return cloneValue(list[0] ?? []);
        return [];
    }
    if (list.length && list.every((value) => deepEqual(value, list[0]))) {
        if (list[0] === undefined)
            return '';
        return cloneValue(list[0]);
    }
    return '';
}

function buildBatchPropertyModel(schemas, models) {
    const schema = intersectObjectSchemas(schemas);
    const model = buildMergedValue(schema, models);
    return { schema, model };
}

function walkChangedLeaves(prev, next, schema, path, out) {
    if (schema?.type === 'object' && schema.properties) {
        const prevObj = isPlainObject(prev) ? prev : {};
        const nextObj = isPlainObject(next) ? next : {};
        for (const key of Object.keys(schema.properties)) {
            walkChangedLeaves(
                prevObj[key],
                nextObj[key],
                schema.properties[key],
                path.concat(key),
                out,
            );
        }
        return;
    }
    if (!deepEqual(prev, next))
        out.push({ path: path.slice(), value: cloneValue(next) });
}

/**
 * Leaf paths whose display value changed. Arrays count as one leaf.
 * @returns {{ path: string[], value: * }[]}
 */
function listChangedLeaves(prev, next, schema) {
    const out = [];
    walkChangedLeaves(prev, next, schema, [], out);
    return out;
}

function setAtPath(root, path, value) {
    if (!root || !path?.length)
        return;
    let cursor = root;
    for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!isPlainObject(cursor[key]))
            cursor[key] = {};
        cursor = cursor[key];
    }
    cursor[path[path.length - 1]] = cloneValue(value);
}

export {
    cloneValue,
    intersectObjectSchemas,
    buildMergedValue,
    buildBatchPropertyModel,
    listChangedLeaves,
    setAtPath,
};
